import z from "zod";
import type { TaskType } from "@remi/enums/task-type";
import type { SunrunAccountId } from "@remi/types/puppeteer";
import type { RespondentRoleOption } from "@remi/types/surveyFeedback";

import { type ErrorDetails, RemiHttpCode } from "./remi-app";

const trpcRouterErrorTypeValues = [
  "UNAUTHENTICATED",
  "INVALID_XACTANALYSIS_AUTH_TOKEN",
  "INVALID_HOVER_WEBHOOK_SECRET",
  "FILE_NOT_FOUND",
  "INVALID_LINK_ID",
  "BID_NOT_FOUND",
  "NOT_IMPLEMENTED",
  "INVALID_THUMBNAIL_FILE_ID",
  "SUNLIGHT_NO_EVENT",
  "SUNLIGHT_HOME_API_SYNC_FAILED",
  "SUNLIGHT_HOME_API_EVENT_MALFORMED",
  "SUNLIGHT_HOME_API_EVENT_DROPPED_NO_PROJECT_ID",
  "NO_VALID_MESSAGES",
  "UNAUTHORIZED",
  "QUOTE_NOT_FOUND",
  "QUOTE_VERSION_NOT_FOUND",
  "SANDBOX_QUOTE_NOT_FOUND",
  "PROJECT_NOT_FOUND",
  "ORGANIZATION_NOT_FOUND",
  "TASK_NOT_FOUND",
  "UNATTACHED_PROXY_PHONE",
  "WEBHOOK_SIGNATURE_MISSING",
  "WEBHOOK_SIGNATURE_MISMATCH",
  "WEBHOOK_VERIFIER_CONFIG_ERROR",
  "WEBHOOK_REPLAY_WINDOW_EXPIRED",
  "INVALID_WEBHOOK_PAYLOAD",
  "UNSUPPORTED_SUNRUN_ACCOUNT_ID",
  "SURVEY_FEEDBACK_NOT_FOUND",
  "STRATEGIC_PARTNER_LOAN_PRODUCT_UNAVAILABLE",
  "HOMEOWNER_EMAIL_MISSING",
  "EAGLEVIEW_VERIFY_URL_HOST_REJECTED",
  "BLOCKED_TASK_TYPE_NOT_ALLOWED",
  "INSTALL_SCHEDULED_MANUAL_DATE_BLOCKED",
  "COLOR_SELECTION_ALREADY_SUBMITTED",
  "MARKETING_LEAD_SOURCE_NOT_ALLOWED",
] as const;

const trpcRouterErrorTypeSchema = z.enum(trpcRouterErrorTypeValues);
export const TrpcRouterErrorType = trpcRouterErrorTypeSchema.enum;
type TrpcRouterErrorType = z.infer<typeof trpcRouterErrorTypeSchema>;

export type TrpcRouterErrors =
  | {
      errorType: typeof TrpcRouterErrorType.UNAUTHENTICATED;
      data: undefined;
    }
  | {
      errorType: typeof TrpcRouterErrorType.INVALID_XACTANALYSIS_AUTH_TOKEN;
      data: {
        authToken: string | string[] | undefined;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.FILE_NOT_FOUND;
      data: {
        fileId: string;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.INVALID_LINK_ID;
      data: {
        linkId: string | string[] | undefined;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.BID_NOT_FOUND;
      data: {
        bidId: number;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.NOT_IMPLEMENTED;
      data: undefined;
    }
  | {
      errorType: typeof TrpcRouterErrorType.INVALID_THUMBNAIL_FILE_ID;
      data: {
        fileId: string;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.SUNLIGHT_NO_EVENT;
      data: undefined;
    }
  | {
      errorType: typeof TrpcRouterErrorType.SUNLIGHT_HOME_API_SYNC_FAILED;
      data: { reason: string };
    }
  | {
      errorType: typeof TrpcRouterErrorType.SUNLIGHT_HOME_API_EVENT_MALFORMED;
      data: {
        zodIssues: string[];
        webhookDeliveryId: string | undefined;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.SUNLIGHT_HOME_API_EVENT_DROPPED_NO_PROJECT_ID;
      data: {
        eventId: string;
        eventName: string;
        objectLevel: string;
        objectId: string;
        webhookDeliveryId: string | undefined;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.NO_VALID_MESSAGES;
      data: undefined;
    }
  | {
      errorType: typeof TrpcRouterErrorType.UNAUTHORIZED;
      data: Record<string, unknown>;
    }
  | {
      errorType: typeof TrpcRouterErrorType.QUOTE_NOT_FOUND;
      data: {
        quoteId: string;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.QUOTE_VERSION_NOT_FOUND;
      data: {
        quoteVersionId: string;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.SANDBOX_QUOTE_NOT_FOUND;
      data: {
        sandboxQuoteId: string;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.PROJECT_NOT_FOUND;
      data: {
        projectId: number;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.ORGANIZATION_NOT_FOUND;
      data: {
        organizationId?: string;
        personId?: string;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.TASK_NOT_FOUND;
      data: {
        bidId: number;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.UNATTACHED_PROXY_PHONE;
      data: {
        proxyPhoneNumber: string | undefined;
        sourceNumber: string | undefined;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.WEBHOOK_SIGNATURE_MISSING;
      data: {
        path: string;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.WEBHOOK_SIGNATURE_MISMATCH;
      data: {
        path: string;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.WEBHOOK_VERIFIER_CONFIG_ERROR;
      data: {
        path: string;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.WEBHOOK_REPLAY_WINDOW_EXPIRED;
      data: {
        path: string;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.MARKETING_LEAD_SOURCE_NOT_ALLOWED;
      data: { source: string };
    }
  | {
      errorType: typeof TrpcRouterErrorType.INVALID_WEBHOOK_PAYLOAD;
      data: {
        path: string;
        parseError: unknown;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.UNSUPPORTED_SUNRUN_ACCOUNT_ID;
      data: {
        partnerAccountId: SunrunAccountId;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.SURVEY_FEEDBACK_NOT_FOUND;
      data: {
        projectId: number;
        respondentRole: RespondentRoleOption;
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.INVALID_HOVER_WEBHOOK_SECRET;
      data: undefined;
    }
  | {
      errorType: typeof TrpcRouterErrorType.STRATEGIC_PARTNER_LOAN_PRODUCT_UNAVAILABLE;
      data: {
        reason: "missing" | "unexpected-fields";
        candidate?: {
          term: number;
          apr: number;
          productType: string;
          isACH?: boolean;
          dealerFee?: number;
          promoAPR?: number;
          promoTerm?: number;
        };
      };
    }
  | {
      errorType: typeof TrpcRouterErrorType.HOMEOWNER_EMAIL_MISSING;
      data: { bidId: number; requestForQuoteId: number; financingId?: string };
    }
  | {
      errorType: typeof TrpcRouterErrorType.EAGLEVIEW_VERIFY_URL_HOST_REJECTED;
      data: { projectId: number; hostname: string | null };
    }
  | {
      errorType: typeof TrpcRouterErrorType.BLOCKED_TASK_TYPE_NOT_ALLOWED;
      data: { projectId: number; blockedTaskType: TaskType };
    }
  | {
      errorType: typeof TrpcRouterErrorType.INSTALL_SCHEDULED_MANUAL_DATE_BLOCKED;
      data: { taskIds: string[]; taskTypes: TaskType[] };
    }
  | {
      errorType: typeof TrpcRouterErrorType.COLOR_SELECTION_ALREADY_SUBMITTED;
      data: { bidId: number };
    };

export const trpcRouterErrorDetails: Record<TrpcRouterErrorType, ErrorDetails> = {
  [TrpcRouterErrorType.UNAUTHENTICATED]: {
    sentryDescription: "User is not authenticated",
    remiHttpCode: RemiHttpCode.UNAUTHORIZED,
  },
  [TrpcRouterErrorType.INVALID_XACTANALYSIS_AUTH_TOKEN]: {
    sentryDescription: "Invalid Xactanalysis auth token",
    remiHttpCode: RemiHttpCode.UNAUTHORIZED,
  },
  [TrpcRouterErrorType.INVALID_HOVER_WEBHOOK_SECRET]: {
    sentryDescription: "Invalid Hover webhook secret",
    remiHttpCode: RemiHttpCode.UNAUTHORIZED,
  },
  [TrpcRouterErrorType.FILE_NOT_FOUND]: {
    sentryDescription: "File not found",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.INVALID_LINK_ID]: {
    sentryDescription: "Invalid link ID",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.BID_NOT_FOUND]: {
    sentryDescription: "Bid not found",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.NOT_IMPLEMENTED]: {
    sentryDescription: "Not implemented",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [TrpcRouterErrorType.INVALID_THUMBNAIL_FILE_ID]: {
    sentryDescription: "Invalid thumbnail file ID",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.SUNLIGHT_NO_EVENT]: {
    sentryDescription: "Sunlight no event",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.SUNLIGHT_HOME_API_SYNC_FAILED]: {
    sentryDescription: "Sunlight Home API webhook: all downstream syncFinancing calls failed",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [TrpcRouterErrorType.SUNLIGHT_HOME_API_EVENT_MALFORMED]: {
    sentryDescription: "Sunlight Home API webhook: event failed schema validation and was dropped",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [TrpcRouterErrorType.SUNLIGHT_HOME_API_EVENT_DROPPED_NO_PROJECT_ID]: {
    sentryDescription: "Sunlight Home API webhook: event had no resolvable projectId and was dropped",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [TrpcRouterErrorType.NO_VALID_MESSAGES]: {
    sentryDescription: "No valid messages",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.UNAUTHORIZED]: {
    sentryDescription: "User does not have permission to access this resource",
    remiHttpCode: RemiHttpCode.FORBIDDEN,
  },
  [TrpcRouterErrorType.QUOTE_NOT_FOUND]: {
    sentryDescription: "Quote not found",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.QUOTE_VERSION_NOT_FOUND]: {
    sentryDescription: "Quote version not found",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.SANDBOX_QUOTE_NOT_FOUND]: {
    sentryDescription: "Sandbox quote not found",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.PROJECT_NOT_FOUND]: {
    sentryDescription: "Project not found",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.ORGANIZATION_NOT_FOUND]: {
    sentryDescription: "Organization not found",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.TASK_NOT_FOUND]: {
    sentryDescription: "Task not found",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.UNATTACHED_PROXY_PHONE]: {
    sentryDescription: "Unattached proxy phone number",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.WEBHOOK_SIGNATURE_MISSING]: {
    sentryDescription: "Webhook signature missing",
    remiHttpCode: RemiHttpCode.UNAUTHORIZED,
  },
  [TrpcRouterErrorType.WEBHOOK_SIGNATURE_MISMATCH]: {
    sentryDescription: "Webhook signature mismatch",
    remiHttpCode: RemiHttpCode.UNAUTHORIZED,
  },
  [TrpcRouterErrorType.WEBHOOK_VERIFIER_CONFIG_ERROR]: {
    sentryDescription: "Webhook verifier is misconfigured: secret/env is missing or invalid",
    remiHttpCode: RemiHttpCode.UNAUTHORIZED,
  },
  [TrpcRouterErrorType.WEBHOOK_REPLAY_WINDOW_EXPIRED]: {
    sentryDescription: "Webhook signature timestamp outside replay window",
    remiHttpCode: RemiHttpCode.UNAUTHORIZED,
  },
  [TrpcRouterErrorType.INVALID_WEBHOOK_PAYLOAD]: {
    sentryDescription: "Invalid webhook payload",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [TrpcRouterErrorType.MARKETING_LEAD_SOURCE_NOT_ALLOWED]: {
    sentryDescription: "Marketing lead source is not accepted on this route",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [TrpcRouterErrorType.UNSUPPORTED_SUNRUN_ACCOUNT_ID]: {
    sentryDescription: "Unsupported Sunrun account ID",
    remiHttpCode: RemiHttpCode.UNAUTHORIZED,
  },
  [TrpcRouterErrorType.SURVEY_FEEDBACK_NOT_FOUND]: {
    sentryDescription: "Survey feedback not found",
    remiHttpCode: RemiHttpCode.NOT_FOUND,
  },
  [TrpcRouterErrorType.STRATEGIC_PARTNER_LOAN_PRODUCT_UNAVAILABLE]: {
    sentryDescription: "Strategic Partner financing row missing monthlyPayment / loan terms when serving loan options",
    remiHttpCode: RemiHttpCode.INTERNAL_SERVER_ERROR,
  },
  [TrpcRouterErrorType.HOMEOWNER_EMAIL_MISSING]: {
    sentryDescription: "Homeowner has no primary email address; cannot submit financing request to Sunlight",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [TrpcRouterErrorType.EAGLEVIEW_VERIFY_URL_HOST_REJECTED]: {
    sentryDescription: "EagleView NeedToId VerifyId URL failed the eagleview.com host allowlist",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [TrpcRouterErrorType.BLOCKED_TASK_TYPE_NOT_ALLOWED]: {
    sentryDescription: "This task type cannot be targeted by a manual blocking task",
    remiHttpCode: RemiHttpCode.BAD_REQUEST,
  },
  [TrpcRouterErrorType.INSTALL_SCHEDULED_MANUAL_DATE_BLOCKED]: {
    sentryDescription: "This date now moves by recording an attempt on the task, not by editing it here",
    remiHttpCode: RemiHttpCode.FORBIDDEN,
  },
  [TrpcRouterErrorType.COLOR_SELECTION_ALREADY_SUBMITTED]: {
    sentryDescription: "Color selection already submitted",
    remiHttpCode: RemiHttpCode.FORBIDDEN,
  },
};
